import React, { useEffect, useState } from 'react';
import apiService from '../../services/api';
import './ManageDailySpiritualInfo.css';

const emptyForm = {
  title: '',
  dateLabel: '',
  content: '',
  isActive: true
};

const ManageDailySpiritualInfo = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const fetchItems = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getAllDailySpiritualInfo();
      if (response.success) {
        setItems(response.data || []);
      } else {
        throw new Error(response.message || 'Failed to fetch spiritual information entries.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      if (editingId) {
        await apiService.updateDailySpiritualInfo(editingId, formData);
      } else {
        await apiService.createDailySpiritualInfo(formData);
      }

      await fetchItems();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      title: item.title || '',
      dateLabel: item.dateLabel || '',
      content: item.content || '',
      isActive: !!item.isActive
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this spiritual info entry?')) return;

    setError(null);
    try {
      await apiService.deleteDailySpiritualInfo(itemId);
      await fetchItems();
      if (editingId === itemId) {
        resetForm();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (item) => {
    setError(null);
    try {
      await apiService.updateDailySpiritualInfo(item._id, {
        title: item.title,
        dateLabel: item.dateLabel || '',
        content: item.content,
        isActive: !item.isActive
      });
      await fetchItems();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="manage-daily-spiritual-info-container">
      <div className="header-section">
        <h2>Daily Spiritual Info / Panchang</h2>
      </div>

      {error ? <div className="error-message">Error: {error}</div> : null}

      <form className="daily-spiritual-form" onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g. Ekadashi Vrat Reminder"
              required
              maxLength={140}
            />
          </div>

          <div className="form-group">
            <label htmlFor="dateLabel">Date Label (optional)</label>
            <input
              id="dateLabel"
              name="dateLabel"
              value={formData.dateLabel}
              onChange={handleChange}
              placeholder="e.g. Tue, 07 May 2026"
              maxLength={80}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="content">Message / Panchang Info</label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleChange}
            placeholder="Write today's spiritual information here..."
            required
            maxLength={1200}
            rows={5}
          />
        </div>

        <div className="form-actions-row">
          <label className="checkbox-container-admin">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
            />
            Publish as active homepage update
          </label>

          <div className="form-action-buttons">
            {editingId ? (
              <button type="button" className="action-btn" onClick={resetForm}>
                Cancel Edit
              </button>
            ) : null}
            <button type="submit" className="btn-primary-admin" disabled={isSaving}>
              {isSaving ? 'Saving...' : editingId ? 'Update Entry' : 'Create Entry'}
            </button>
          </div>
        </div>
      </form>

      <div className="entries-table-wrapper">
        <table className="entries-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Date Label</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="4" className="no-results">Loading entries...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-results">No entries yet.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item._id}>
                  <td data-label="Title">
                    <strong>{item.title}</strong>
                    <p className="entry-content-preview">{item.content}</p>
                  </td>
                  <td data-label="Date Label">{item.dateLabel || '-'}</td>
                  <td data-label="Status">
                    <span className={`status-badge ${item.isActive ? 'active' : 'inactive'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td data-label="Actions" className="action-cell">
                    <button className="action-btn" onClick={() => handleEdit(item)}>
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button className="action-btn" onClick={() => handleToggleActive(item)}>
                      <i className="fas fa-bullhorn"></i> {item.isActive ? 'Unpublish' : 'Publish'}
                    </button>
                    <button className="action-btn btn-delete" onClick={() => handleDelete(item._id)}>
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageDailySpiritualInfo;
